/*
 * packing-list.js — Zoomlite Packing List Generator (v3)
 *
 * Client-side assembly engine. Reads the content bank serialised by
 * sections/packing-list-quiz.liquid (blocks / destinations / picks),
 * evaluates switch rules, computes per-person quantities, and renders
 * one of three views: landing hero, quiz, or the section-tab list.
 *
 * URL contract v3: ?p=solo&d=italy&l=14&m=2026-10&a=beach,work&lug=carry_on
 *   All core params present -> list renders directly.
 *   Some params -> quiz, prefilled. None -> landing + quiz.
 *   No legacy (?t=) mapping - owner call, pre-launch only.
 *
 * switch_rule grammar (kept tiny and dumb):
 *   always | party:a|b | activity:a,b | luggage:a|b | region:a,b |
 *   beach_flag | len_gte:N   ("+" = AND, ";" = OR)
 *
 * Klaviyo: subscribe + "Built Packing List" event fire from the gate in
 * the Before-you-go band; the event payload keeps the `stages` key
 * (now section labels) so template VpFrcg needs no structural change.
 *
 * Changelog:
 *   2026-08-17  Initial build; loader.
 *   2026-09-23  Stage 2: name, email gate, Klaviyo, gated PDF; scrubbing;
 *               print overlay fixes; month dropdown includes current month.
 *   2026-09-24  v2: guide design language, accordion grid, tick
 *               persistence, weight card removed, A4 print doc, resend.
 *   2026-10-01  v3 rewrite per owner mockups: landing hero; new question
 *               model (party / where-when / activities multi / luggage);
 *               section tabs (Essentials, Wear, Care, Your trip) with
 *               weather + recommendation side rail; Before-you-go band
 *               with the gate inside it; per-person quantities ("x7
 *               each"); per-section add-an-item; v3 URL params.
 *   2026-10-01  Design fixes: landing is its own screen (quiz hidden
 *               until Create is pressed); trust row icons; How it works
 *               modal; warmer Q1/Q2 sub-copy; Save-as-PDF moved into the
 *               gate beside the email action (gating unchanged).
 *   2026-10-01  Owner copy pass: landing/modal/quiz copy per owner doc;
 *               quiz name field removed (name lives in the gate only);
 *               quiz CTA now "Create my packing list"; AU spelling
 *               (traveller/travelling) and no custom/personalised/smart
 *               wording - standing style rule.
 *   2026-10-02  Q3 reworded "Anything special planned?" and gains an
 *               exclusive "Nothing in particular" answer (URL a=none) so
 *               the Create button no longer forces an activity pick.
 *   2026-10-02  Quantity anomalies: adapter + power bank lose their
 *               counts; single-item clothing lines (warm layer, rain
 *               jacket, thermals) get "each" for couples/families; tops
 *               shows the bank's own sub-line; laundry note only on trips
 *               over a week; every recommendation card is headed
 *               "Worth packing" (black badge and old lead-ins removed);
 *               "Designed end-to-end in Melbourne" footer removed.
 *   2026-10-02  Compact phone masthead + tiles live in the CSS (no JS
 *               change; a tap-to-scroll idea was tried and dropped).
 *   2026-10-16  First-order discount code removed everywhere: unlocked
 *               gate row, Klaviyo event payload, A4 print footer.
 */
(function () {
  'use strict';

  var root = document.querySelector('.zl-plq');
  if (!root) return;

  function readData(name) {
    var el = document.querySelector('script[data-pl-data="' + name + '"]');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch (e) {
      console.error('packing-list: bad JSON for ' + name, e);
      return [];
    }
  }

  var BLOCKS = readData('blocks');
  var DESTS = readData('destinations');
  var PICKS = readData('picks');

  var MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var REGION_HANDLES = ['europe', 'se-asia', 'east-asia', 'pacific', 'americas', 'uk-ireland', 'domestic'];

  var PARTIES = [
    { v: 'solo', label: 'Just me' },
    { v: 'couple', label: 'Two adults' },
    { v: 'family', label: 'Family with children' }
  ];
  var ACTIVITIES = [
    { v: 'sightseeing', label: 'Sightseeing' },
    { v: 'beach', label: 'Beach and pool' },
    { v: 'outdoors', label: 'Outdoors and adventure' },
    { v: 'work', label: 'Work' },
    { v: 'events', label: 'Dining and events' },
    { v: 'snow', label: 'Snow and skiing' },
    { v: 'cruise', label: 'Cruise' }
  ];
  var LUGGAGE = [
    { v: 'carry_on', label: 'Carry-on only' },
    { v: 'checked', label: 'Checked luggage' },
    { v: 'both', label: 'Both' }
  ];
  var LENGTHS = [
    { v: 3, label: 'Weekend' },
    { v: 7, label: 'A week' },
    { v: 14, label: 'Two weeks' },
    { v: 30, label: 'A month or more' }
  ];
  var LEN_WORDS = { 3: 'weekend', 7: 'one-week', 14: 'two-week', 30: 'month-long' };

  var SECTIONS = [
    { key: 'essentials', label: 'Essentials', sub: 'Documents, money and tech.' },
    { key: 'wear', label: 'Wear', sub: 'Clothing, shoes and layers for this trip.' },
    { key: 'care', label: 'Care', sub: 'Toiletries and health.' },
    { key: 'trip', label: 'Your trip', sub: 'Chosen for your answers.' }
  ];
  var TRUST_ICONS = {
    clock: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"/><path d="M8 4.5V8l2.3 1.6"/></svg>',
    mail: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><rect x="1.8" y="3.5" width="12.4" height="9" rx="1.5"/><path d="m2.5 4.5 5.5 4.3 5.5-4.3"/></svg>',
    print: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 6V2.5h7V6M4.5 11H3a1.2 1.2 0 0 1-1.2-1.2V7.2A1.2 1.2 0 0 1 3 6h10a1.2 1.2 0 0 1 1.2 1.2v2.6A1.2 1.2 0 0 1 13 11h-1.5"/><rect x="4.5" y="9.5" width="7" height="4" rx="0.8"/></svg>'
  };

  var ICONS = {
    essentials: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5"/><path d="M1.5 6.5h13M4 9.5h3"/></svg>',
    wear: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><path d="M5.5 2.5 2 4.8l1.4 2.1 1.6-1v7.6h6V5.9l1.6 1L14 4.8 10.5 2.5c-.6.8-1.6 1.2-2.5 1.2s-1.9-.4-2.5-1.2Z"/></svg>',
    care: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2.2S4 7 4 10a4 4 0 0 0 8 0c0-3-4-7.8-4-7.8Z"/></svg>',
    trip: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"/><path d="m10.5 5.5-1.6 3.4-3.4 1.6 1.6-3.4 3.4-1.6Z"/></svg>'
  };

  // Formula keys that scale per person get an "each" suffix for couples
  // and families (quantities stay per person - owner decision).
  var PER_PERSON_KEYS = { tops: 1, pants: 1, underwear: 1, socks: 1, sleepwear: 1, swimwear: 1, one: 1, two: 1, three: 1 };

  var LS_NAME = 'zl-packing-list-name';
  var LS_UNLOCK = 'zl-packing-list-unlocked';
  var LS_CUSTOM_V3 = 'zl-packing-list-custom-v3';
  var LS_CUSTOM_OLD = 'zl-packing-list-custom';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var KL_REVISION = '2024-10-15';
  var CONFIG = {
    klKey: root.getAttribute('data-kl-key') || '',
    klList: root.getAttribute('data-kl-list') || ''
  };

  // ---------------------------------------------------------------------
  // helpers

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function icon(cls, svg) {
    var s = el('span', cls);
    s.innerHTML = svg; // static markup defined above, never user input
    return s;
  }
  function lsGet(key) {
    try { return localStorage.getItem(key) || ''; } catch (e) { return ''; }
  }
  function lsSet(key, v) {
    try { localStorage.setItem(key, v); } catch (e) { /* private mode */ }
  }

  function labelFor(list, v) {
    for (var i = 0; i < list.length; i++) if (list[i].v === v) return list[i].label;
    return v;
  }

  function findDest(handle) {
    var i;
    for (i = 0; i < DESTS.length; i++) if (DESTS[i].handle === handle) return DESTS[i];
    for (i = 0; i < DESTS.length; i++) if (DESTS[i].handle === 'europe') return DESTS[i];
    return DESTS[0] || null;
  }

  function ruleFires(rule, ctx) {
    if (!rule || rule === 'always') return true;
    return String(rule).split(';').some(function (orClause) {
      return orClause.split('+').every(function (clause) {
        clause = clause.trim();
        if (!clause || clause === 'always') return true;
        if (clause.charAt(0) === '!') return !ruleFires(clause.slice(1), ctx);
        if (clause === 'beach_flag') return !!ctx.beach;
        if (clause.indexOf('party:') === 0) return clause.slice(6).split(/[|,]/).indexOf(ctx.party) !== -1;
        if (clause.indexOf('activity:') === 0) {
          return clause.slice(9).split(/[|,]/).some(function (a) {
            return (ctx.activities || []).indexOf(a) !== -1;
          });
        }
        if (clause.indexOf('luggage:') === 0) return clause.slice(8).split(/[|,]/).indexOf(ctx.luggage) !== -1;
        if (clause.indexOf('region:') === 0) return clause.slice(7).split(/[|,]/).indexOf(ctx.region) !== -1;
        if (clause.indexOf('len_gte:') === 0) return ctx.len >= parseInt(clause.slice(8), 10);
        if (clause.indexOf('traveler:') === 0) return false; // pre-v3 grammar, no longer produced
        return false;
      });
    });
  }

  // "Nothing in particular" is an answer in its own right: it satisfies Q3
  // and is exclusive with every activity. Stored as the single value
  // 'none' (URL: a=none); no rule ever matches it.
  var NO_ACTIVITY = { v: 'none', label: 'Nothing in particular' };
  var ACTIVITY_VALUES = ACTIVITIES.concat([NO_ACTIVITY]).map(function (a) { return a.v; });

  function readParams() {
    var q = new URLSearchParams(location.search);
    var out = { p: null, d: null, l: null, m: null, a: [], lug: null, any: false };
    ['p', 'd', 'l', 'm', 'a', 'lug'].forEach(function (k) { if (q.get(k)) out.any = true; });
    var p = q.get('p');
    if (p && PARTIES.some(function (x) { return x.v === p; })) out.p = p;
    if (q.get('d')) out.d = q.get('d').toLowerCase();
    var l = parseInt(q.get('l'), 10);
    if ([3, 7, 14, 30].indexOf(l) !== -1) out.l = l;
    var m = q.get('m');
    if (m && /^\d{4}-\d{2}$/.test(m)) {
      var mi = parseInt(m.slice(5), 10);
      if (mi >= 1 && mi <= 12) out.m = m;
    }
    var a = q.get('a');
    if (a) {
      out.a = a.split(',').filter(function (x) { return ACTIVITY_VALUES.indexOf(x) !== -1; });
      var realOnes = out.a.filter(function (x) { return x !== NO_ACTIVITY.v; });
      out.a = realOnes.length ? realOnes : out.a.slice(0, 1);
    }
    var lug = q.get('lug');
    if (lug && LUGGAGE.some(function (x) { return x.v === lug; })) out.lug = lug;
    return out;
  }

  function writeParams(a) {
    var q = new URLSearchParams();
    q.set('p', a.p); q.set('d', a.d); q.set('l', a.l); q.set('m', a.m);
    q.set('a', a.a.join(',')); q.set('lug', a.lug);
    // keep the preview theme param while testing on an unpublished theme
    var prev = new URLSearchParams(location.search).get('preview_theme_id');
    if (prev) q.set('preview_theme_id', prev);
    history.pushState(null, '', location.pathname + '?' + q.toString());
  }

  function answersKey(a) {
    return [a.p, a.d, a.l, a.m, a.a.join('-'), a.lug].join('.');
  }

  // ---------------------------------------------------------------------
  // persistence: ticks per answer combination, custom items per section

  var tickStore = { key: null, map: {} };
  function loadTicks(a) {
    tickStore.key = 'zl-packing-list-ticks:' + answersKey(a);
    try { tickStore.map = JSON.parse(localStorage.getItem(tickStore.key)) || {}; } catch (e) { tickStore.map = {}; }
  }
  function setTick(id, on) {
    if (on) tickStore.map[id] = 1; else delete tickStore.map[id];
    try { localStorage.setItem(tickStore.key, JSON.stringify(tickStore.map)); } catch (e) { /* private mode */ }
  }

  function loadCustom() {
    var store = null;
    try { store = JSON.parse(localStorage.getItem(LS_CUSTOM_V3)); } catch (e) { /* ignore */ }
    if (!store || typeof store !== 'object') store = {};
    // one-time migration of the old flat list into Your trip
    try {
      var old = JSON.parse(localStorage.getItem(LS_CUSTOM_OLD));
      if (old && old.length) {
        store.trip = (store.trip || []).concat(old);
        localStorage.removeItem(LS_CUSTOM_OLD);
        localStorage.setItem(LS_CUSTOM_V3, JSON.stringify(store));
      }
    } catch (e) { /* ignore */ }
    return store;
  }
  function saveCustom(store) {
    try { localStorage.setItem(LS_CUSTOM_V3, JSON.stringify(store)); } catch (e) { /* private mode */ }
  }

  var lastRender = null; // {a, ctx, fired, sectionPicks} for print + payload

  // ---------------------------------------------------------------------
  // engines

  function quantities(a, laundry) {
    var len = a.l;
    var effective = laundry ? Math.min(len, 7) : len;
    return {
      tops: Math.min(Math.ceil(len / 2), laundry ? 7 : 999),
      pants: Math.max(2, Math.ceil(effective / 3)),
      underwear: effective,
      socks: effective,
      sleepwear: len >= 14 ? 2 : 1,
      swimwear: 2,
      one: 1,
      two: 2,
      three: 3
    };
  }

  function qtyLabel(item, ctx) {
    var q = item.qty && ctx.qty[item.qty] != null ? ctx.qty[item.qty] : null;
    if (q == null || q <= 0) return null;
    var each = ctx.party !== 'solo' && PER_PERSON_KEYS[item.qty] ? ' each' : '';
    return '× ' + q + each;
  }

  // [DRAFT] markers flag unwritten copy in the bank; they stay in the
  // data but never reach the customer-facing render, email payload or
  // PDF. The old [VIDHI] spelling is still scrubbed for safety.
  function scrubPlaceholders(str) {
    return str ? String(str).replace(/\[(?:DRAFT|VIDHI)\]\s*/g, '') : str;
  }

  function fillTokens(str, ctx) {
    if (!str) return str;
    return scrubPlaceholders(str)
      .replace(/,\s*\{name\}/g, ctx.name ? ', ' + ctx.name : '')
      .replace(/\{name\}/g, ctx.name || '')
      .replace(/\{month\}/g, ctx.monthName)
      .replace(/\{destination\}/g, ctx.destName)
      .replace(/\{plug\}/g, ctx.plug || '')
      .replace(/\{days\}/g, String(ctx.len))
      .replace(/\{tops\}/g, String(ctx.qty ? ctx.qty.tops : ''));
  }

  function itemVisible(item, ctx) {
    if (item.rule && !ruleFires(item.rule, ctx)) return false;
    if (item.flag) {
      if (item.flag === 'warm') return !!ctx.climate.warm;
      if (item.flag === 'thermals') return !!ctx.climate.thermals;
      if (item.flag === 'rain') return !!ctx.climate.rain;
    }
    return true;
  }

  // ---------------------------------------------------------------------
  // quiz + landing

  var quizWrap = root.querySelector('[data-pl-quiz]');
  var quizCard = root.querySelector('[data-pl-quiz-card]');
  var listWrap = root.querySelector('[data-pl-list]');
  // Nothing preselected (owner call): the progress bar starts honest at
  // 0 of 4 and every answer is a real choice. Lists opened from a link
  // without an activities param still fall back to sightseeing.
  var quizState = { p: null, d: null, l: null, m: null, a: [], lug: null };

  function monthOptions(selected) {
    // current month + next 11; include the selected value if outside range
    var out = [], now = new Date(), seen = {};
    for (var i = 0; i <= 11; i++) {
      var dt = new Date(now.getFullYear(), now.getMonth() + i, 1);
      var v = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0');
      out.push({ v: v, label: MONTH_NAMES[dt.getMonth()] + ' ' + dt.getFullYear() });
      seen[v] = true;
    }
    if (selected && !seen[selected]) {
      var mi = parseInt(selected.slice(5), 10) - 1;
      out.unshift({ v: selected, label: MONTH_NAMES[mi] + ' ' + selected.slice(0, 4) });
    }
    return out;
  }

  function answeredCount() {
    var n = 0;
    if (quizState.p) n++;
    if (quizState.d && quizState.l && quizState.m) n++;
    if (quizState.a.length) n++;
    if (quizState.lug) n++;
    return n;
  }

  function syncQuiz() {
    var n = answeredCount();
    var btn = quizWrap.querySelector('.pl-quiz__go');
    if (btn) btn.disabled = n < 4;
    var fill = quizWrap.querySelector('.pl-progress__fill');
    if (fill) fill.style.width = (n * 25) + '%';
    var count = quizWrap.querySelector('.pl-progress__count');
    if (count) count.textContent = n + ' of 4 answered';
  }

  function qCard(num, title, subline) {
    var card = el('div', 'pl-qcard');
    var head = el('div', 'pl-qcard__head');
    head.appendChild(el('span', 'pl-qcard__num', String(num)));
    var t = el('div', 'pl-qcard__titles');
    t.appendChild(el('div', 'pl-qcard__title', title));
    t.appendChild(el('div', 'pl-qcard__sub', subline));
    head.appendChild(t);
    card.appendChild(head);
    return card;
  }

  function pillGroup(options, isOn, onPick) {
    var pills = el('div', 'pl-q__pills');
    options.forEach(function (o) {
      var b = el('button', 'pl-pill', o.label);
      b.type = 'button';
      b.setAttribute('data-v', o.v);
      if (isOn(o.v)) b.classList.add('is-on');
      b.addEventListener('click', function () {
        onPick(o.v, b, pills);
        syncQuiz();
      });
      pills.appendChild(b);
    });
    return pills;
  }

  function labelledSelect(labelText, build) {
    var wrap = el('label', 'pl-field');
    wrap.appendChild(el('span', 'pl-field__label', labelText));
    var sel = el('select', 'pl-select');
    build(sel);
    wrap.appendChild(sel);
    return { wrap: wrap, sel: sel };
  }

  function openHowModal() {
    var overlay = el('div', 'pl-modal');
    var card = el('div', 'pl-modal__card');
    var x = el('button', 'pl-modal__x', '×');
    x.type = 'button';
    x.setAttribute('aria-label', 'Close');
    card.appendChild(x);
    card.appendChild(el('h3', 'pl-modal__title', 'How it works'));
    [
      ['Tell us about your trip', 'Four quick questions about where you’re going, when you’re going and how you travel.'],
      ['Get a list built for your trip', 'We’ll cover what most travellers need, with extras based on your trip. Tick off what applies to you and leave anything you don’t need.'],
      ['Keep it handy', 'Email your list to yourself, or save or print a copy for later.']
    ].forEach(function (s, i) {
      var row = el('div', 'pl-modal__step');
      row.appendChild(el('span', 'pl-modal__num', String(i + 1)));
      var t = el('div');
      t.appendChild(el('div', 'pl-modal__step-t', s[0]));
      t.appendChild(el('div', 'pl-modal__step-s', s[1]));
      row.appendChild(t);
      card.appendChild(row);
    });
    overlay.appendChild(card);
    function onKey(e) { if (e.key === 'Escape') close(); }
    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    x.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);
    x.focus();
  }

  function renderQuiz(withLanding) {
    listWrap.hidden = true;
    quizWrap.hidden = false;
    quizWrap.querySelectorAll('.pl-landing, .pl-quiz__intro').forEach(function (n) { n.remove(); });
    quizCard.textContent = '';

    var intro = el('div', 'pl-quiz__intro');
    intro.appendChild(el('h2', 'pl-quiz__heading', 'Let’s create your packing list.'));
    intro.appendChild(el('p', 'pl-quiz__sub', 'Tell us a little about your trip. We’ll take care of the list.'));
    quizWrap.insertBefore(intro, quizCard);

    // The landing is its own screen: the quiz stays hidden until
    // "Create my packing list" is pressed (owner call, matches mockup 1).
    if (withLanding) {
      intro.hidden = true;
      quizCard.hidden = true;
      var hero = el('div', 'pl-landing');
      hero.appendChild(el('h1', 'pl-landing__h', 'A packing list made for your trip.'));
      hero.appendChild(el('p', 'pl-landing__sub',
        'Answer four quick questions and we’ll build your list around where you’re going, when you’re travelling and anything special you have planned.'));
      var ctas = el('div', 'pl-landing__ctas');
      var go = el('button', 'pl-btn', 'Create my packing list');
      go.type = 'button';
      go.addEventListener('click', function () {
        hero.remove();
        intro.hidden = false;
        quizCard.hidden = false;
        window.scrollTo({ top: 0, behavior: 'auto' });
      });
      ctas.appendChild(go);
      var how = el('button', 'pl-btn pl-btn--ghost', 'How it works');
      how.type = 'button';
      how.addEventListener('click', openHowModal);
      ctas.appendChild(how);
      hero.appendChild(ctas);
      var trust = el('div', 'pl-landing__trust');
      [['clock', 'About one minute'], ['mail', 'Email it to yourself'], ['print', 'Save or print']]
        .forEach(function (t) {
          var item = el('span', 'pl-landing__trust-item');
          item.appendChild(icon('pl-landing__trust-icon', TRUST_ICONS[t[0]]));
          item.appendChild(el('span', null, t[1]));
          trust.appendChild(item);
        });
      hero.appendChild(trust);
      quizWrap.insertBefore(hero, quizWrap.firstChild);
    }

    var prog = el('div', 'pl-progress');
    prog.appendChild(el('span', 'pl-progress__count', '0 of 4 answered'));
    var bar = el('div', 'pl-progress__bar');
    bar.appendChild(el('span', 'pl-progress__fill'));
    quizCard.appendChild(prog);
    quizCard.appendChild(bar);

    // Q1 party
    var q1 = qCard(1, 'Who are you packing for?', 'We’ll adjust the list for who’s travelling.');
    q1.appendChild(pillGroup(PARTIES,
      function (v) { return quizState.p === v; },
      function (v, b, pills) {
        quizState.p = v;
        pills.querySelectorAll('.pl-pill').forEach(function (p) { p.classList.remove('is-on'); });
        b.classList.add('is-on');
      }));
    quizCard.appendChild(q1);

    // Q2 where and when
    var q2 = qCard(2, 'Where and when are you going?', 'We’ll account for the weather and length of your trip.');
    var fields = el('div', 'pl-qcard__fields');
    var destSel = labelledSelect('Destination', function (sel) {
      var ph = el('option', null, 'Where are you going?');
      ph.value = ''; ph.disabled = true; ph.selected = true;
      sel.appendChild(ph);
      DESTS.filter(function (d) { return REGION_HANDLES.indexOf(d.handle) === -1; })
        .sort(function (a, b) { return a.name.localeCompare(b.name); })
        .forEach(function (d) {
          var o = el('option', null, d.name); o.value = d.handle; sel.appendChild(o);
        });
      var grp = document.createElement('optgroup');
      grp.label = 'Somewhere else';
      REGION_HANDLES.forEach(function (h) {
        if (h === 'domestic') return;
        var d = DESTS.filter(function (x) { return x.handle === h; })[0];
        if (!d) return;
        var o = el('option', null, d.name); o.value = d.handle; grp.appendChild(o);
      });
      sel.appendChild(grp);
    });
    destSel.sel.value = quizState.d || '';
    destSel.sel.addEventListener('change', function () { quizState.d = destSel.sel.value || null; syncQuiz(); });
    fields.appendChild(destSel.wrap);

    var monthSel = labelledSelect('When', function (sel) {
      var ph = el('option', null, 'Choose a month');
      ph.value = ''; ph.disabled = true; ph.selected = true;
      sel.appendChild(ph);
      monthOptions(quizState.m).forEach(function (o) {
        var opt = el('option', null, o.label); opt.value = o.v; sel.appendChild(opt);
      });
    });
    monthSel.sel.value = quizState.m || '';
    monthSel.sel.addEventListener('change', function () { quizState.m = monthSel.sel.value || null; syncQuiz(); });
    fields.appendChild(monthSel.wrap);

    var lenSel = labelledSelect('Trip length', function (sel) {
      var ph = el('option', null, 'How long?');
      ph.value = ''; ph.disabled = true; ph.selected = true;
      sel.appendChild(ph);
      LENGTHS.forEach(function (o) {
        var opt = el('option', null, o.label); opt.value = String(o.v); sel.appendChild(opt);
      });
    });
    lenSel.sel.value = quizState.l ? String(quizState.l) : '';
    lenSel.sel.addEventListener('change', function () {
      quizState.l = lenSel.sel.value ? parseInt(lenSel.sel.value, 10) : null;
      syncQuiz();
    });
    fields.appendChild(lenSel.wrap);
    q2.appendChild(fields);
    quizCard.appendChild(q2);

    // Q3 activities (multi)
    var q3 = qCard(3, 'Anything special planned?', 'Choose anything that should be included in your packing list.');
    q3.appendChild(pillGroup(ACTIVITIES.concat([NO_ACTIVITY]),
      function (v) { return quizState.a.indexOf(v) !== -1; },
      function (v, b, pills) {
        var i = quizState.a.indexOf(v);
        if (v === NO_ACTIVITY.v) {
          quizState.a = i === -1 ? [NO_ACTIVITY.v] : [];
        } else {
          quizState.a = quizState.a.filter(function (x) { return x !== NO_ACTIVITY.v; });
          i = quizState.a.indexOf(v);
          if (i === -1) quizState.a.push(v); else quizState.a.splice(i, 1);
        }
        pills.querySelectorAll('.pl-pill').forEach(function (p) {
          p.classList.toggle('is-on', quizState.a.indexOf(p.getAttribute('data-v')) !== -1);
        });
      }));
    quizCard.appendChild(q3);

    // Q4 luggage
    var q4 = qCard(4, 'What luggage are you taking?', 'We’ll keep your list realistic for the space you have.');
    q4.appendChild(pillGroup(LUGGAGE,
      function (v) { return quizState.lug === v; },
      function (v, b, pills) {
        quizState.lug = v;
        pills.querySelectorAll('.pl-pill').forEach(function (p) { p.classList.remove('is-on'); });
        b.classList.add('is-on');
      }));
    quizCard.appendChild(q4);

    // First name is collected only at the email gate (owner call 2026-10-01);
    // the quiz asks nothing personal.
    var go = el('button', 'pl-btn pl-quiz__go', 'Create my packing list');
    go.type = 'button';
    go.addEventListener('click', function () {
      if (go.disabled) return;
      onQuizComplete({
        p: quizState.p, d: quizState.d, l: quizState.l, m: quizState.m,
        a: quizState.a.slice(), lug: quizState.lug
      });
    });
    quizCard.appendChild(go);
    syncQuiz();
  }

  function onQuizComplete(answers) {
    writeParams(answers);
    showBuildLoader(answers, function () { renderList(answers); });
  }

  function showBuildLoader(a, done) {
    var dest = findDest(a.d);
    var monthIdx = parseInt(a.m.slice(5), 10) - 1;
    quizWrap.hidden = true;
    listWrap.hidden = false;
    listWrap.textContent = '';
    // The quiz is tall on phones and the Build button sits near its foot;
    // when the quiz collapses into the short loader the browser keeps the
    // old scroll offset, leaving the user staring at the page footer.
    // Jump to the top NOW so the loader is what they watch.
    window.scrollTo({ top: 0, behavior: 'auto' });
    var box = el('div', 'pl-loader');
    box.appendChild(el('div', 'pl-loader__spin'));
    var msg = el('div', 'pl-loader__msg', 'Reading your answers');
    box.appendChild(msg);
    listWrap.appendChild(box);
    var msgs = [
      'Checking ' + (dest ? dest.name : 'your destination') + ' in ' + MONTH_NAMES[monthIdx],
      'Sizing quantities to ' + a.l + ' days',
      'Finishing your list'
    ];
    var i = 0;
    var iv = setInterval(function () {
      if (i < msgs.length) msg.textContent = msgs[i++];
    }, 550);
    setTimeout(function () {
      clearInterval(iv);
      done();
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, 1800);
  }

  // ---------------------------------------------------------------------
  // Klaviyo (unchanged mechanics; new property set)

  function specProperties(a, ctx) {
    return {
      party: a.p,
      activities: a.a.filter(function (x) { return x !== NO_ACTIVITY.v; }),
      luggage: a.lug,
      traveler_type: a.lug, // continuity for pre-v3 segments
      trip_region: ctx.region,
      destination: ctx.destName,
      trip_length: a.l,
      next_trip_month: a.m,
      signup_category: 'packing_list'
    };
  }

  function buildEmailPayload(a, ctx, fired, custom) {
    var stages = [];
    SECTIONS.forEach(function (section) {
      var blocks = sectionBlocks(fired, section.key).map(function (b) {
        return payloadBlock(b, ctx);
      });
      var extras = (custom[section.key] || []);
      if (extras.length) {
        blocks.push({ title: 'Your own additions', subtitle: '', items: extras.map(function (t) { return { name: t, sub: '', qty: null }; }) });
      }
      if (blocks.length) stages.push({ label: section.label, blocks: blocks });
    });
    var beforeItems = beforeTaskItems(fired, ctx).map(function (it) {
      return { name: it.name, sub: it.sub || '', qty: null };
    });
    if (beforeItems.length) {
      stages.push({ label: 'Before you go', blocks: [{ title: 'Final checks', subtitle: '', items: beforeItems }] });
    }

    var picks = [];
    SECTIONS.forEach(function (section) {
      sectionPicks(fired, ctx, section.key).forEach(function (p) {
        if (!p.product) return;
        var url = p.product.url || '';
        if (url && url.indexOf('/') === 0) url = location.origin + url;
        picks.push({ title: p.product.title, url: url, copy: fillTokens(p.copy, ctx) });
      });
    });

    var q = new URLSearchParams();
    q.set('p', a.p); q.set('d', a.d); q.set('l', a.l); q.set('m', a.m);
    q.set('a', a.a.join(',')); q.set('lug', a.lug);
    var lenWord = LEN_WORDS[a.l] || a.l + '-day';

    return {
      heading: 'Your ' + lenWord + ' ' + ctx.destName + ' list',
      list_url: location.origin + location.pathname + '?' + q.toString(),
      climate_note: scrubPlaceholders(ctx.climate.note || ''),
      stages: stages,
      picks: picks,
      custom_items: []
    };
  }

  function klaviyoSubscribe(email, name, props) {
    if (!CONFIG.klKey || !CONFIG.klList) return Promise.resolve('skipped');
    var attributes = { email: email, properties: props };
    if (name) attributes.first_name = name;
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'packing_list',
          profile: { data: { type: 'profile', attributes: attributes } }
        },
        relationships: { list: { data: { type: 'list', id: CONFIG.klList } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(CONFIG.klKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KL_REVISION },
      body: JSON.stringify(payload)
    });
  }

  function klaviyoEvent(email, name, props, listPayload) {
    var profileAttrs = { email: email, properties: props };
    if (name) profileAttrs.first_name = name;
    var payload = {
      data: {
        type: 'event',
        attributes: {
          properties: listPayload,
          metric: { data: { type: 'metric', attributes: { name: 'Built Packing List' } } },
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        }
      }
    };
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(CONFIG.klKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KL_REVISION },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok && res.status !== 202) throw new Error('event ' + res.status);
      return res;
    });
  }

  // ---------------------------------------------------------------------
  // list building blocks

  function sectionBlocks(fired, sectionKey) {
    return fired.filter(function (b) { return b.section === sectionKey; })
      .sort(function (x, y) { return (x.sort || 0) - (y.sort || 0); });
  }

  function visibleItems(block, ctx) {
    return (block.items || []).filter(function (it) { return itemVisible(it, ctx); })
      .map(function (it) { return it; });
  }

  function payloadBlock(b, ctx) {
    var subtitle = b.subtitle;
    if (b.key === 'b2_clothing' && ctx.laundry && ctx.len > 7) subtitle = ctx.laundryLine;
    return {
      title: b.title,
      subtitle: subtitle ? fillTokens(subtitle, ctx) : '',
      items: visibleItems(b, ctx).map(function (it) {
        return {
          name: fillTokens(it.name, ctx),
          sub: it.sub ? fillTokens(it.sub, ctx) : '',
          qty: qtyLabel(it, ctx)
        };
      })
    };
  }

  function beforeTaskItems(fired, ctx) {
    var out = [];
    sectionBlocks(fired, 'before').forEach(function (b) {
      visibleItems(b, ctx).forEach(function (it) {
        out.push({ name: fillTokens(it.name, ctx), sub: it.sub ? fillTokens(it.sub, ctx) : '', id: b.key + '|' + it.name });
      });
    });
    return out;
  }

  // Up to two recommendations per section, lowest priority number first.
  // Picks whose bound block fires are kept; a blank bound means "always
  // eligible for this section".
  function sectionPicks(fired, ctx, sectionKey) {
    var firedKeys = {};
    fired.forEach(function (b) { firedKeys[b.key] = true; });
    return PICKS.filter(function (p) {
      return p.section === sectionKey && ruleFires(p.rule, ctx) &&
        (!p.bound || firedKeys[p.bound] || !BLOCKS.some(function (b) { return b.key === p.bound; }));
    }).sort(function (x, y) { return (x.priority || 99) - (y.priority || 99); })
      .slice(0, 2);
  }

  function itemRow(item, ctx, opts) {
    opts = opts || {};
    var row = el('div', 'pl-item');
    var tick = el('button', 'pl-tick');
    tick.type = 'button';
    tick.setAttribute('aria-label', 'Mark as packed');
    if (opts.done) { tick.classList.add('is-done'); row.classList.add('is-done'); }
    tick.addEventListener('click', function () {
      tick.classList.toggle('is-done');
      row.classList.toggle('is-done');
      if (opts.id) setTick(opts.id, tick.classList.contains('is-done'));
      if (opts.onChange) opts.onChange();
    });
    row.appendChild(tick);
    var name = el('div', 'pl-item__name', fillTokens(item.name, ctx));
    if (item.sub) name.appendChild(el('small', null, fillTokens(item.sub, ctx)));
    row.appendChild(name);
    var q = qtyLabel(item, ctx);
    if (q) row.appendChild(el('span', 'pl-qty', q));
    return row;
  }

  // ---------------------------------------------------------------------
  // the list view

  function renderList(a) {
    var dest = findDest(a.d);
    if (!dest) { renderQuiz(true); return; }
    if (!a.a.length) a.a = ['sightseeing'];
    var monthIdx = parseInt(a.m.slice(5), 10) - 1;
    var climate = (dest.months || {})[MONTH_KEYS[monthIdx]] || { low: '', high: '', note: '', warm: false, thermals: false, rain: false };

    var ctx = {
      party: a.p, activities: a.a, luggage: a.lug,
      region: dest.region, beach: !!dest.beach, len: a.l,
      destName: dest.name, plug: dest.plug, monthName: MONTH_NAMES[monthIdx],
      climate: climate, name: lsGet(LS_NAME)
    };

    var byKey = {};
    BLOCKS.forEach(function (b) { byKey[b.key] = b; });
    ctx.laundry = byKey.c4_laundry ? ruleFires(byKey.c4_laundry.rule, ctx) : false;
    ctx.laundryLine = (byKey.c4_laundry && byKey.c4_laundry.subtitle) || 'Counts assume a laundry stop around day 7.';
    ctx.qty = quantities(a, ctx.laundry);

    loadTicks(a);
    var custom = loadCustom();
    var fired = BLOCKS.filter(function (b) { return b.section && ruleFires(b.rule, ctx); });
    lastRender = { a: a, ctx: ctx, fired: fired, custom: custom };

    var activeSection = SECTIONS[0].key;

    listWrap.textContent = '';
    quizWrap.hidden = true;
    listWrap.hidden = false;

    // masthead (owner mockup: grey card, overall progress bar across all
    // four section tiles, trip meta column with Edit trip)
    var heading = (ctx.name ? ctx.name + ', your ' : 'Your ') + dest.name + ' packing list';
    document.title = heading + ' | Zoomlite';
    var masthead = el('div', 'pl-mast');
    var mastMain = el('div', 'pl-mast__main');
    mastMain.appendChild(el('h1', 'pl-mast__h', heading));
    var mprog = el('div', 'pl-mast__prog');
    var mcount = el('span', 'pl-mast__count', '');
    mcount.setAttribute('data-pl-mcount', '');
    var mbar = el('div', 'pl-mast__bar');
    var mfill = el('span', 'pl-mast__fill');
    mfill.setAttribute('data-pl-mfill', '');
    mbar.appendChild(mfill);
    var mpct = el('span', 'pl-mast__pct', '');
    mpct.setAttribute('data-pl-mpct', '');
    mprog.appendChild(mcount);
    mprog.appendChild(mbar);
    mprog.appendChild(mpct);
    mastMain.appendChild(mprog);
    masthead.appendChild(mastMain);

    var mastMeta = el('div', 'pl-mast__meta');
    [['Destination', dest.name],
     ['Trip', a.l + ' days · ' + ctx.monthName],
     ['Luggage', labelFor(LUGGAGE, a.lug)]].forEach(function (kv) {
      var r = el('div', 'pl-mast__row');
      r.appendChild(el('span', 'pl-mast__k', kv[0]));
      r.appendChild(el('span', 'pl-mast__v', kv[1]));
      mastMeta.appendChild(r);
    });
    var edit = el('button', 'pl-mast__edit', 'Edit trip');
    edit.type = 'button';
    edit.addEventListener('click', function () {
      quizState = { p: a.p, d: a.d, l: a.l, m: a.m, a: a.a.slice(), lug: a.lug };
      renderQuiz(false);
      quizWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    mastMeta.appendChild(edit);
    masthead.appendChild(mastMeta);
    listWrap.appendChild(masthead);

    // pack-by-section header
    var totalItems = 0;
    SECTIONS.forEach(function (s) {
      sectionBlocks(fired, s.key).forEach(function (b) { totalItems += visibleItems(b, ctx).length; });
      totalItems += (custom[s.key] || []).length;
    });
    var pbsHead = el('div', 'pl-pbs');
    var pbsTitles = el('div', 'pl-pbs__titles');
    pbsTitles.appendChild(el('h2', 'pl-pbs__h', 'Pack by section'));
    pbsTitles.appendChild(el('div', 'pl-pbs__sub', 'Choose a section to review and tick off its items.'));
    pbsHead.appendChild(pbsTitles);
    pbsHead.appendChild(el('div', 'pl-pbs__meta', SECTIONS.length + ' sections · ' + totalItems + ' items'));
    listWrap.appendChild(pbsHead);

    // tiles
    var tiles = el('div', 'pl-tiles');
    listWrap.appendChild(tiles);

    // panel + rail
    var body = el('div', 'pl-body');
    var panel = el('div', 'pl-panel');
    var rail = el('div', 'pl-rail');
    body.appendChild(panel);
    body.appendChild(rail);
    listWrap.appendChild(body);

    function sectionStats(sectionKey) {
      var items = 0, done = 0;
      sectionBlocks(fired, sectionKey).forEach(function (b) {
        visibleItems(b, ctx).forEach(function (it) {
          items++;
          if (tickStore.map[b.key + '|' + it.name]) done++;
        });
      });
      (custom[sectionKey] || []).forEach(function (t) {
        items++;
        if (tickStore.map['custom|' + sectionKey + '|' + t]) done++;
      });
      return { items: items, done: done };
    }

    function paintTiles() {
      tiles.textContent = '';
      SECTIONS.forEach(function (s) {
        var st = sectionStats(s.key);
        var t = el('button', 'pl-tile' + (s.key === activeSection ? ' is-active' : ''));
        t.type = 'button';
        t.appendChild(icon('pl-tile__icon', ICONS[s.key]));
        t.appendChild(el('span', 'pl-tile__name', s.label));
        var meta = el('span', 'pl-tile__meta');
        meta.appendChild(el('span', null, st.done + ' of ' + st.items));
        meta.appendChild(el('span', 'pl-tile__open', s.key === activeSection ? 'Open' : 'Open →'));
        t.appendChild(meta);
        t.addEventListener('click', function () {
          activeSection = s.key;
          paintTiles();
          paintPanel();
          paintRail();
        });
        tiles.appendChild(t);
      });
    }

    function refresh() {
      paintTiles();
      var chip = panel.querySelector('.pl-panel__count');
      if (chip) {
        var st = sectionStats(activeSection);
        chip.textContent = st.done + ' of ' + st.items + ' packed';
        chip.classList.toggle('is-packed', st.done > 0);
      }
      // masthead progress across all four tiles
      var ti = 0, td = 0;
      SECTIONS.forEach(function (s) {
        var all = sectionStats(s.key);
        ti += all.items; td += all.done;
      });
      var mc = listWrap.querySelector('[data-pl-mcount]');
      if (mc) {
        var pct = ti ? Math.round((td / ti) * 100) : 0;
        mc.textContent = td + ' of ' + ti + ' packed';
        var f = listWrap.querySelector('[data-pl-mfill]');
        if (f) f.style.width = pct + '%';
        var pe = listWrap.querySelector('[data-pl-mpct]');
        if (pe) pe.textContent = pct + '%';
      }
    }

    function paintPanel() {
      panel.textContent = '';
      var section = SECTIONS.filter(function (s) { return s.key === activeSection; })[0];
      var head = el('div', 'pl-panel__head');
      var titles = el('div');
      titles.appendChild(el('h3', 'pl-panel__title', section.label));
      titles.appendChild(el('div', 'pl-panel__sub', section.sub));
      head.appendChild(titles);
      var st = sectionStats(section.key);
      var chip = el('span', 'pl-count pl-panel__count', st.done + ' of ' + st.items + ' packed');
      if (st.done > 0) chip.classList.add('is-packed');
      head.appendChild(chip);
      panel.appendChild(head);

      var blocks = sectionBlocks(fired, section.key);
      blocks.forEach(function (b, idx) {
        if (blocks.length > 1) {
          var g = el('div', 'pl-group');
          g.appendChild(el('span', null, b.title));
          panel.appendChild(g);
        }
        var subtitle = b.subtitle;
        if (b.key === 'b2_clothing' && ctx.laundry && ctx.len > 7) subtitle = ctx.laundryLine;
        if (subtitle) panel.appendChild(el('div', 'pl-group__sub', fillTokens(subtitle, ctx)));
        visibleItems(b, ctx).forEach(function (it) {
          var id = b.key + '|' + it.name;
          panel.appendChild(itemRow(it, ctx, { id: id, done: !!tickStore.map[id], onChange: refresh }));
        });
      });

      // custom items for this section
      (custom[section.key] || []).forEach(function (text, idx) {
        var id = 'custom|' + section.key + '|' + text;
        var row = itemRow({ name: text }, ctx, { id: id, done: !!tickStore.map[id], onChange: refresh });
        var rm = el('button', 'pl-custom__rm', '×');
        rm.type = 'button';
        rm.setAttribute('aria-label', 'Remove ' + text);
        rm.addEventListener('click', function () {
          custom[section.key].splice(idx, 1);
          saveCustom(custom);
          paintPanel();
          refresh();
        });
        row.appendChild(rm);
        panel.appendChild(row);
      });

      var form = el('div', 'pl-custom__form');
      var input = el('input', 'pl-custom__input');
      input.type = 'text';
      input.placeholder = 'Add an item';
      input.maxLength = 80;
      var add = el('button', 'pl-custom__add', '+ Add');
      add.type = 'button';
      function commit() {
        var v = input.value.trim();
        if (!v) return;
        custom[section.key] = custom[section.key] || [];
        custom[section.key].push(v);
        saveCustom(custom);
        input.value = '';
        paintPanel();
        refresh();
      }
      add.addEventListener('click', commit);
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') commit(); });
      form.appendChild(input);
      form.appendChild(add);
      panel.appendChild(form);
    }

    function paintRail() {
      rail.textContent = '';
      var weather = el('div', 'pl-rail__card pl-weather');
      weather.appendChild(el('span', 'pl-rail__tag', 'Trip weather'));
      weather.appendChild(el('div', 'pl-weather__range', climate.low + ' to ' + climate.high + '°C'));
      var note = scrubPlaceholders(climate.note || '');
      if (note) weather.appendChild(el('p', 'pl-weather__note', note));
      rail.appendChild(weather);

      sectionPicks(fired, ctx, activeSection).forEach(function (pick, idx) {
        if (!pick.product) return;
        var rec = el('div', 'pl-rail__card pl-rec');
        rec.appendChild(el('div', 'pl-rec__lead', 'Worth packing'));
        if (pick.product.image) {
          var imWrap = el('div', 'pl-rec__img');
          var im = document.createElement('img');
          im.src = pick.product.image;
          im.alt = pick.product.title;
          im.loading = 'lazy';
          imWrap.appendChild(im);
          rec.appendChild(imWrap);
        }
        var line = el('div', 'pl-rec__line');
        line.appendChild(el('span', 'pl-rec__title', pick.product.title));
        if (pick.product.price) line.appendChild(el('span', 'pl-rec__price', pick.product.price));
        rec.appendChild(line);
        rec.appendChild(el('p', 'pl-rec__copy', fillTokens(pick.copy, ctx)));
        var btn = el('a', 'pl-btn pl-rec__btn', 'View product');
        btn.href = pick.product.url;
        rec.appendChild(btn);
        rail.appendChild(rec);
      });
    }

    paintTiles();
    paintPanel();
    paintRail();
    refresh();

    // carry-on nudge
    if (a.lug === 'carry_on') {
      var nudge = el('div', 'pl-nudge');
      var copy = el('div', 'pl-nudge__copy');
      copy.appendChild(el('div', 'pl-eyebrow', 'Travelling carry-on only'));
      copy.appendChild(el('p', null,
        'Have you looked at a Zoomlite carry-on? Our lightest shell leaves more of the 7 kg allowance for what you actually pack.'));
      nudge.appendChild(copy);
      var nbtn = el('a', 'pl-btn', 'See the carry-ons');
      nbtn.href = '/collections/carry-on-luggage';
      nudge.appendChild(nbtn);
      listWrap.appendChild(nudge);
    }

    // before-you-go band with the gate inside it
    listWrap.appendChild(beforeBand(a, ctx, fired, custom));
  }

  // ---------------------------------------------------------------------
  // before-you-go band + gate

  function beforeBand(a, ctx, fired, custom) {
    var band = el('div', 'pl-band');
    var head = el('div', 'pl-band__head');
    var titles = el('div');
    titles.appendChild(el('h3', 'pl-band__title', 'Before you go'));
    titles.appendChild(el('div', 'pl-band__sub', 'Final checks for the day you leave.'));
    head.appendChild(titles);
    band.appendChild(head);

    var tasks = el('div', 'pl-band__grid');
    beforeTaskItems(fired, ctx).forEach(function (it) {
      var chip = el('div', 'pl-task');
      var tick = el('button', 'pl-tick');
      tick.type = 'button';
      tick.setAttribute('aria-label', 'Mark as done');
      if (tickStore.map[it.id]) { tick.classList.add('is-done'); chip.classList.add('is-done'); }
      tick.addEventListener('click', function () {
        tick.classList.toggle('is-done');
        chip.classList.toggle('is-done');
        setTick(it.id, tick.classList.contains('is-done'));
      });
      chip.appendChild(tick);
      var nm = el('span', 'pl-task__name', it.name);
      chip.appendChild(nm);
      chip.title = it.sub || '';
      tasks.appendChild(chip);
    });
    band.appendChild(tasks);

    // gate area
    var gate = el('div', 'pl-gate');
    band.appendChild(gate);

    // The PDF lives INSIDE the gate, beside the email action, so the
    // email-unlocks-PDF rule reads at a glance (owner call 2026-10-01).
    function makePdf(locked, err) {
      var b = el('button', 'pl-gate__pdf' + (locked ? ' is-locked' : ''), 'Save as PDF');
      b.type = 'button';
      b.addEventListener('click', function () {
        if (locked) {
          if (err) err.textContent = 'Enter your email above to unlock the download.';
          return;
        }
        window.print();
      });
      return b;
    }

    function paintUnlocked(email, justSent) {
      gate.textContent = '';
      var line = justSent
        ? (ctx.name ? ctx.name + ', it' : 'It') + '’s on its way to ' + email + '. The link in that email rebuilds this exact list.'
        : 'You’re all set as ' + email + '. Email yourself this version of the list, or save it as a PDF.';
      gate.appendChild(el('p', 'pl-gate__copy', line));
      var actions = el('div', 'pl-gate__actions');
      var uerr = el('div', 'pl-gate__err', '');
      if (!justSent) {
        var again = el('button', 'pl-btn', 'Email me this list too');
        again.type = 'button';
        again.addEventListener('click', function () {
          again.disabled = true;
          again.textContent = 'Sending…';
          uerr.textContent = '';
          var props = specProperties(a, ctx);
          var listPayload = buildEmailPayload(a, ctx, fired, custom);
          Promise.resolve(klaviyoSubscribe(email, ctx.name || '', props))
            .then(null, function () { return null; })
            .then(function () { return klaviyoEvent(email, ctx.name || '', props, listPayload); })
            .then(function () { paintUnlocked(email, true); })
            .then(null, function () {
              again.disabled = false;
              again.textContent = 'Email me this list too';
              uerr.textContent = 'Something went wrong. Please try again in a moment.';
            });
        });
        actions.appendChild(again);
      }
      actions.appendChild(makePdf(false));
      gate.appendChild(actions);
      gate.appendChild(uerr);
    }

    function paintLocked() {
      gate.textContent = '';
      var err = el('div', 'pl-gate__err', '');
      gate.appendChild(el('p', 'pl-gate__copy',
        'Email yourself the full list, including anything you added, and unlock the PDF download.'));
      var form = el('div', 'pl-gate__form');
      var nameInput = el('input', 'pl-gate__input');
      nameInput.type = 'text';
      nameInput.placeholder = 'First name (optional)';
      nameInput.maxLength = 40;
      nameInput.autocomplete = 'given-name';
      nameInput.value = ctx.name || '';
      var emailInput = el('input', 'pl-gate__input pl-gate__input--email');
      emailInput.type = 'email';
      emailInput.placeholder = 'Email address';
      emailInput.autocomplete = 'email';
      var btn = el('button', 'pl-gate__btn', 'Email me my list');
      btn.type = 'button';
      function submit() {
        var email = emailInput.value.trim();
        var name = nameInput.value.trim();
        if (!EMAIL_RE.test(email)) { err.textContent = 'That email address does not look right.'; return; }
        err.textContent = '';
        btn.disabled = true;
        btn.textContent = 'Sending…';
        if (name) { lsSet(LS_NAME, name); ctx.name = name; }
        var props = specProperties(a, ctx);
        var listPayload = buildEmailPayload(a, ctx, fired, custom);
        Promise.resolve(klaviyoSubscribe(email, name, props))
          .then(null, function () { return null; })
          .then(function () { return klaviyoEvent(email, name, props, listPayload); })
          .then(function () {
            lsSet(LS_UNLOCK, email);
            paintUnlocked(email, true);
          })
          .then(null, function () {
            btn.disabled = false;
            btn.textContent = 'Email me my list';
            err.textContent = 'Something went wrong. Please try again in a moment.';
          });
      }
      btn.addEventListener('click', submit);
      emailInput.addEventListener('keydown', function (e) { if (e.key === 'Enter') submit(); });
      form.appendChild(nameInput);
      form.appendChild(emailInput);
      form.appendChild(btn);
      gate.appendChild(form);
      gate.appendChild(err);
      gate.appendChild(el('p', 'pl-gate__consent',
        'We’ll send your list, plus occasional packing tips. Unsubscribe anytime.'));
      gate.appendChild(makePdf(true, err));
    }

    var unlockedEmail = lsGet(LS_UNLOCK);
    if (unlockedEmail) paintUnlocked(unlockedEmail, false);
    else paintLocked();
    return band;
  }

  // ---------------------------------------------------------------------
  // print: dedicated flat A4 document

  function buildPrintDoc() {
    if (!lastRender) return null;
    var a = lastRender.a, ctx = lastRender.ctx;
    var payload = buildEmailPayload(a, ctx, lastRender.fired, lastRender.custom);
    var doc = el('div', 'pl-print');
    doc.appendChild(el('div', 'pl-print__eyebrow', 'Zoomlite'));
    doc.appendChild(el('div', 'pl-print__title',
      ctx.name ? ctx.name + ', y' + payload.heading.slice(1) : payload.heading));
    var metaBits = [ctx.destName, a.l + ' days', labelFor(PARTIES, a.p), labelFor(LUGGAGE, a.lug), 'Departing ' + ctx.monthName];
    if (payload.climate_note) metaBits.push(payload.climate_note);
    doc.appendChild(el('div', 'pl-print__meta', metaBits.join(' · ')));

    var cols = el('div', 'pl-print__cols');
    payload.stages.forEach(function (stage) {
      stage.blocks.forEach(function (block) {
        var blk = el('div', 'pl-print__block');
        blk.appendChild(el('h3', null, block.title === 'Final checks' ? 'Before you go' : block.title));
        if (block.subtitle) blk.appendChild(el('p', 'sub', block.subtitle));
        block.items.forEach(function (it) {
          var row = el('div', 'pl-print__row');
          row.appendChild(el('span', 'box'));
          var nm = el('span', 'nm', it.name);
          if (it.sub) nm.appendChild(el('small', null, ' · ' + it.sub));
          row.appendChild(nm);
          if (it.qty) row.appendChild(el('span', 'q', it.qty));
          blk.appendChild(row);
        });
        cols.appendChild(blk);
      });
    });
    doc.appendChild(cols);

    var foot = el('div', 'pl-print__foot');
    foot.appendChild(el('span', null, payload.list_url.replace(/^https?:\/\//, '')));
    doc.appendChild(foot);
    return doc;
  }

  var printHidden = [];
  window.addEventListener('beforeprint', function () {
    if (lastRender && !root.querySelector('.pl-print')) {
      var docEl = buildPrintDoc();
      if (docEl) root.appendChild(docEl);
    }
    var all = document.body.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var n = all[i];
      if (n === root || root.contains(n) || n.contains(root)) continue;
      var cls = typeof n.className === 'string' ? n.className : '';
      var cs;
      try { cs = getComputedStyle(n); } catch (e) { continue; }
      if (cs.display === 'none') continue;
      if (cs.position === 'fixed' || cs.position === 'sticky' ||
          cls.indexOf('kl-private') !== -1 || cls.indexOf('klaviyo') !== -1 ||
          n.getAttribute('role') === 'dialog') {
        printHidden.push({ n: n, style: n.getAttribute('style') });
        n.style.setProperty('display', 'none', 'important');
      }
    }
  });
  window.addEventListener('afterprint', function () {
    var printDoc = root.querySelector('.pl-print');
    if (printDoc) printDoc.parentNode.removeChild(printDoc);
    printHidden.forEach(function (h) {
      if (h.style == null) h.n.removeAttribute('style');
      else h.n.setAttribute('style', h.style);
    });
    printHidden = [];
  });

  // ---------------------------------------------------------------------
  // boot

  function boot() {
    var p = readParams();
    quizState = { p: p.p, d: p.d, l: p.l, m: p.m, a: p.a, lug: p.lug };
    if (p.p && p.d && p.l && p.m && p.lug) {
      renderList({ p: p.p, d: p.d, l: p.l, m: p.m, a: p.a.length ? p.a : ['sightseeing'], lug: p.lug });
    } else {
      renderQuiz(!p.any);
    }
  }

  window.addEventListener('popstate', boot);
  boot();
})();
